<form [formGroup]="form" (ngSubmit)="review()" class="menu-workspace" appVenueColour [primaryColour]="venue.primaryColour">
    <header class="menu-workspace__header">
        <h1 id="customer-menu-title" class="menu-title h3 m-0">{{ menu.displayName }}</h1>
        <div class="menu-workspace__context">
            <div class="d-flex align-items-baseline justify-content-md-end flex-wrap gap-2">
                @if (menuCourseLimitsDisplay) {
                    <strong data-testid="menu-course-limits">{{ menuCourseLimitsDisplay }}</strong>
                    <span aria-hidden="true">·</span>
                }
                <span data-testid="selected-course-count" aria-live="polite">{{ selectedCourseCount }} {{ selectedCourseCount === 1 ? 'course' : 'courses' }} selected</span>
            </div>
            @if (showMenuCourseValidationError && menuCourseValidationError) {
                <p class="text-danger" role="alert" data-testid="menu-course-validation-error">{{ menuCourseValidationError }}</p>
            }
        </div>
    </header>
    <div class="menu-workspace__layout">
        <nav class="menu-workspace__nav d-none d-lg-block" aria-label="Menu courses" data-testid="menu-course-navigation">
            <h4 class="menu-workspace__nav-title">Courses</h4>
            @for (courseGroup of itemsByCourse; track courseGroup.courseId) {
                <div class="menu-workspace__nav-course">
                    <button class="menu-course-link" type="button" (click)="scrollToCourse(courseGroup.courseId)">
                        <span class="menu-course-link__name">{{ courseGroup.courseName }}</span>
                        @if (isCourseExcludedFromCount(courseGroup.courseId)) { <span class="menu-workspace__badge">Extra</span> }
                        @if (isCourseComplete(courseGroup.courseId)) { <i class="bi bi-check-circle-fill text-success" role="img" aria-label="Complete"></i> }
                    </button>
                    @for (item of courseGroup.items; track item.menuItem.id) {
                        @if (isProductSelected(item.menuItem.id)) {
                            <div class="menu-nav-choice">
                                <span>{{ item.consumable.displayName }}</span>
                                <button type="button" class="menu-selection__remove" [attr.aria-label]="'Remove ' + item.consumable.displayName" (click)="removeProduct(item.menuItem.id)">×</button>
                            </div>
                        }
                    }
                </div>
            }
        </nav>
        <div class="menu-workspace__body" data-testid="menu-scroll-area">
            <section class="menu-guest" aria-labelledby="menu-guest-title">
                <header class="menu-course__header mb-0">
                    <h4 id="menu-guest-title">Your details</h4>
                </header>
                <div>
                    @if (!isNameReadOnly) {
                        <label class="form-label" for="coverName">Person Name</label>
                        <input id="coverName" class="form-control" formControlName="name" placeholder="Enter name" />
                    } @else {
                        <div class="form-label">Person Name</div>
                        <p>{{ prefilledName }}</p>
                    }
                </div>
                <div>
                    <label class="form-label" for="coverNotes">Dietary Requirements <span class="fw-normal">(optional)</span></label>
                    <textarea id="coverNotes" class="form-control" rows="3" formControlName="notes" placeholder="Enter dietary requirements"></textarea>
                </div>
            </section>
            <hr class="my-4 border-secondary" />
            @if (menu.description) { <p class="menu-workspace__description">{{ menu.description }}</p> }
            <main class="menu-workspace__courses" formGroupName="productSelection">
                @for (courseGroup of itemsByCourse; track courseGroup.courseId) {
                    <section class="menu-course" #courseSection tabindex="-1" [attr.data-course-id]="courseGroup.courseId" [attr.aria-label]="courseGroup.courseName">
                        <header class="menu-course__header">
                            <div class="d-flex align-items-center flex-wrap gap-2">
                                <h4>{{ courseGroup.courseName }}</h4>
                                @if (isCourseExcludedFromCount(courseGroup.courseId)) { <span class="menu-workspace__badge" data-testid="course-extra-badge">Extra</span> }
                            </div>
                            @if (courseGroup.courseDescription) { <p style="white-space: pre-line" data-testid="course-description">{{ courseGroup.courseDescription }}</p> }
                            @if (getCourseLimitsDisplay(courseGroup.courseId); as limits) { <p>{{ limits }}{{ hasCourseLimits ? ' if choosing this course' : '' }}</p> }
                            @if (getCourseValidationError(courseGroup.courseId); as error) { <p class="text-danger" role="alert">{{ error }}</p> }
                        </header>
                        <div class="menu-course__dishes">
                            @for (item of courseGroup.items; track item.menuItem.id) {
                                <article class="menu-dish menu-dish--choice" [class.menu-dish--selected]="isProductSelected(item.menuItem.id)">
                                    <label class="menu-dish__choice" [class.menu-dish__choice--disabled]="isProductDisabled(item.menuItem.id, courseGroup.courseId)" [for]="'dish-' + item.menuItem.id">
                                        <input class="form-check-input" type="checkbox" [id]="'dish-' + item.menuItem.id" [formControlName]="item.menuItem.id" [attr.aria-label]="item.consumable.displayName || 'Item'" />
                                        <span>{{ item.consumable.displayName || 'Item' }}</span>
                                    </label>
                                    @if (item.descriptionFull) {
                                        <p class="menu-dish__description">{{ form.get('ui.expandedDescriptions.' + item.expandedControlName)?.value ? item.descriptionFull : item.descriptionShort }}{{ !form.get('ui.expandedDescriptions.' + item.expandedControlName)?.value && item.canTruncate ? '…' : '' }}</p>
                                        @if (item.canTruncate) {
                                            <button class="menu-text-action" type="button" [attr.aria-expanded]="form.get('ui.expandedDescriptions.' + item.expandedControlName)?.value" (click)="toggleDescription(item.expandedControlName)">{{ form.get('ui.expandedDescriptions.' + item.expandedControlName)?.value ? 'Read less' : 'Read more' }}</button>
                                        }
                                    }
                                    @if (item.modifierGroups.length > 0 && isProductSelected(item.menuItem.id)) {
                                        <div class="menu-dish__modifiers" [class.menu-dish__modifiers--revealed]="form.get(['productSelection', item.menuItem.id])?.dirty" [formGroup]="modifierSelectionGroup!">
                                            <div [formGroupName]="item.menuItem.id">
                                                @for (group of item.modifierGroups; track group.id) {
                                                    <fieldset class="menu-dish__modifier-group">
                                                        <legend>{{ group.name }}</legend>
                                                        @if (getModifierGroupLimitsDisplay(group); as limits) { <p>{{ limits }}</p> }
                                                        @if (getModifierGroupValidationError(item.menuItem.id, group); as error) { <p class="text-danger" role="alert">{{ error }}</p> }
                                                        <div class="menu-dish__modifier-options">
                                                            @for (modifierAndSkuId of group.modifiers; track modifierAndSkuId.modifier.id) {
                                                                @if (modifierAndSkuId.primarySkuId; as mid) {
                                                                    <label class="menu-dish__modifier" [class.menu-dish__choice--disabled]="isModifierDisabled(item.menuItem.id, group, mid)">
                                                                        <input class="form-check-input" type="checkbox" [formControlName]="mid" [attr.aria-label]="modifierAndSkuId.modifier.displayName" />
                                                                        <span>{{ modifierAndSkuId.modifier.displayName }}@if (modifierAndSkuId.modifier.description) { <small>{{ modifierAndSkuId.modifier.description }}</small> }</span>
                                                                    </label>
                                                                }
                                                            }
                                                        </div>
                                                    </fieldset>
                                                }
                                            </div>
                                        </div>
                                    }
                                </article>
                            }
                        </div>
                    </section>
                }
            </main>
        </div>
    </div>
    <footer class="menu-workspace__footer">
        <div class="menu-workspace__footer-actions">
            <button type="button" class="btn btn-outline-secondary" (click)="dismiss()" [disabled]="saving">Cancel</button>
            <button type="submit" class="btn btn-primary" [disabled]="saving">{{ saving ? 'Saving...' : 'Confirm Choices' }}</button>
        </div>
    </footer>
    @if (saving) {
        <div class="menu-workspace__saving" role="status"><span class="spinner-border" aria-hidden="true"></span><span>Saving choices…</span></div>
    }
</form>
<ng-template #reviewSummary let-modal>
    <div class="d-flex flex-column overflow-hidden" appVenueColour [primaryColour]="venue.primaryColour">
        <div class="modal-header">
            <h2 class="modal-title h5" id="review-menu-title">Review your choices</h2>
            <button type="button" class="btn-close" aria-label="Keep editing" (click)="modal.dismiss()"></button>
        </div>
        <div class="modal-body">
            <p class="mb-1 fw-semibold">{{ menu.displayName }}</p>
            @if (form.value.name) { <p class="text-muted">{{ form.value.name }}</p> }
            <ul class="list-unstyled mb-4">
                @for (item of selectedItems; track item.menuItemId) {
                    <li class="py-3 border-bottom">
                        <div class="small text-muted mb-1">{{ item.courseName }}{{ item.extra ? ' · Extra' : '' }}</div>
                        <div class="fw-semibold">{{ item.productName }}</div>
                        @for (group of item.modifiers; track $index) {
                            <div class="small mt-1">{{ group.name }}: {{ group.choices.join(', ') }}</div>
                        }
                    </li>
                } @empty { <li class="py-3">No dishes selected.</li> }
            </ul>
            @if (form.value.notes) {
                <h3 class="h6">Dietary requirements</h3>
                <p class="text-break" style="white-space: pre-line">{{ form.value.notes }}</p>
            }
            @if (!canEditAfterSubmit) {
                <div class="alert alert-warning mb-0" data-testid="menu-submit-warning">You won’t be able to edit your choices here after submitting. Please contact the restaurant if you need to make changes.</div>
            }
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary w-auto" (click)="modal.dismiss()">Keep editing</button>
            <button type="button" class="btn btn-primary w-auto" (click)="modal.close(true)">Submit choices</button>
        </div>
    </div>
</ng-template>
<ng-template #discardChanges let-modal>
    <div class="modal-header">
        <h2 class="modal-title h5" id="discard-menu-title">Leave without saving?</h2>
    </div>
    <div class="modal-body">Your changes to this guest’s choices will be lost.</div>
    <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary w-auto" (click)="modal.dismiss()">Keep editing</button>
        <button type="button" class="btn btn-primary w-auto" (click)="modal.close(true)">Leave without saving</button>
    </div>
</ng-template>
